import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import SetUpReminder from '../components/SetUpReminder';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import { CURRENCIES, getCurrencySymbol } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'annually', label: 'Annually' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'six-monthly', label: 'Every 6 months' },
];

export default function EditDebit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [debitData, setDebitData] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    amount: '',
    payment_day: '',
    frequency: 'monthly',
    notes: '',
    currency: 'GBP'
  });

  useEffect(() => {
    fetchDebit();
  }, [id]);

  const fetchDebit = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/direct-debits/${id}`);
      setDebitData(data);
      setFormData({
        company_name: data.company_name,
        amount: data.amount.toString(),
        payment_day: data.payment_day.toString(),
        frequency: data.frequency,
        notes: data.notes || '',
        currency: data.currency || 'GBP'
      });
    } catch (err) {
      toast.error('Failed to load direct debit');
      navigate('/debits');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name.trim()) {
      toast.error('Please enter a company name');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.payment_day || parseInt(formData.payment_day) < 1 || parseInt(formData.payment_day) > 31) {
      toast.error('Please enter a valid payment day (1-31)');
      return;
    }

    setSaving(true);

    try {
      await axios.put(
        `${API_URL}/api/direct-debits/${id}`,
        {
          company_name: formData.company_name.trim(),
          amount: parseFloat(formData.amount),
          payment_day: parseInt(formData.payment_day),
          frequency: formData.frequency,
          notes: formData.notes.trim() || null,
          currency: formData.currency
        },
        
      );

      toast.success('Changes saved successfully!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to save changes';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(
      "Mark this debit as cancelled?\n\n" +
      "This moves the record to your Cancelled list — you can restore it later.\n\n" +
      "IMPORTANT: this does NOT stop the actual payment or cancel any calendar reminders you set up.\n\n" +
      "To fully stop this debit, please also:\n" +
      "  1. Contact your bank or the company to cancel the payment\n" +
      "  2. Open your phone/Google/Outlook calendar and delete the recurring reminder\n\n" +
      "Continue marking it as cancelled in MDD?"
    )) {
      return;
    }

    setDeleting(true);

    try {
      await axios.delete(`${API_URL}/api/direct-debits/${id}`);
      toast.success('Marked as cancelled. Remember to contact your bank AND delete the calendar reminder.', { duration: 6000 });
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to update direct debit';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout showNav>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout showNav>
      <div className="px-4 py-6 max-w-lg mx-auto">
        <h1 className="font-heading font-bold text-dark text-2xl mb-6">
          Edit Direct Debit
        </h1>

        <form onSubmit={handleSubmit} className="card-default p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Company Name
            </label>
            <Input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="e.g. Netflix, Spotify, Gym"
              data-testid="company-name-input"
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Amount
            </label>
            <div className="flex gap-2">
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                data-testid="currency-select"
                className="input-field w-24 rounded-xl px-2 py-2 text-sm border border-border bg-white"
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                ))}
              </select>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                data-testid="amount-input"
                className="input-field flex-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Payment Day (1-31)
            </label>
            <Input
              type="number"
              min="1"
              max="31"
              value={formData.payment_day}
              onChange={(e) => {
                let val = e.target.value;
                if (val && parseInt(val) > 31) val = '31';
                setFormData({ ...formData, payment_day: val });
              }}
              placeholder="15"
              data-testid="payment-day-input"
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Frequency
            </label>
            <div className="flex flex-wrap gap-2" data-testid="frequency-selector">
              {FREQUENCIES.map((freq) => (
                <button
                  key={freq.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, frequency: freq.value })}
                  data-testid={`freq-${freq.value}`}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    formData.frequency === freq.value
                      ? 'bg-primary text-white'
                      : 'bg-primary-light text-primary-dark hover:bg-primary/20'
                  }`}
                >
                  {freq.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Notes (optional)
            </label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes..."
              data-testid="notes-input"
              className="input-field w-full min-h-[100px]"
            />
          </div>

          <Button
            type="submit"
            disabled={saving}
            data-testid="save-changes-btn"
            className="btn-primary w-full py-3 text-base"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </form>

        {/* SET UP REMINDER — shared component */}
        {debitData && <SetUpReminder debit={debitData} />}

        {/* Mark as Cancelled (soft-cancel — preserved in Cancelled history) */}
        <Button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          data-testid="delete-debit-btn"
          className="btn-destructive w-full py-4 text-base mt-6"
        >
          {deleting ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Trash2 className="w-5 h-5" />
              Mark as Cancelled
            </span>
          )}
        </Button>
        <p className="text-xs text-text-light text-center mt-2 leading-relaxed">
          This updates your record in MDD only. To fully stop this debit, contact your bank <strong>and</strong> delete the recurring reminder from your phone/Google/Outlook calendar.
        </p>
      </div>
    </PageLayout>
  );
}
